import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Pencil, Loader2 } from "lucide-react";
import CrudSection from "@/components/CrudSection";
import { StatusBadge, fmtDate, InspectionBadge } from "@/components/common";
import NoteCell from "@/components/NoteCell";
import InlineEdit from "@/components/InlineEdit";
import { PageHeader } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { updateResource } from "@/lib/api";
import AttachmentsCell from "@/components/AttachmentsCell";

const STATUS = ["inspection", "service", "repair", "up_to_date"];
const STAGE = ["due_for", "overdue", "scheduled", "in_shop", "complete"];

// ---- shared column builders ----
const stamp = () => new Date().toLocaleDateString([], { month: "short", year: "numeric" });

// Current service/repair needed (freely replaceable) + one-click "Done -> Log" that appends it
// to the append-only history (never wipes the past). History also shows on the Z# hover.
const ServiceCell = ({ row }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(row.service_needed || "");
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["fleet"] });
  const cur = (row.service_needed || "").trim();
  const save = async () => {
    setBusy(true);
    try { await updateResource("fleet", row.id, { service_needed: text.trim() }); refresh(); toast.success("Saved"); setOpen(false); }
    catch { toast.error("Couldn't save"); } finally { setBusy(false); }
  };
  const logDone = async () => {
    if (!cur) { toast.error("Nothing to log"); return; }
    setBusy(true);
    try {
      const logs = [...(row.repair_logs || []), `${cur} (${stamp()})`];
      await updateResource("fleet", row.id, { repair_logs: logs, service_needed: "" });
      refresh(); toast.success("Logged to history"); setOpen(false);
    } catch { toast.error("Couldn't log"); } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setText(row.service_needed || ""); }}>
      <PopoverTrigger asChild>
        <button data-testid="fleet-service-cell" className="text-left group max-w-[220px] focus:outline-none">
          {cur ? <span className="text-sm text-gray-800 group-hover:underline decoration-dotted decoration-gray-300">{cur}</span>
               : <span className="inline-flex items-center gap-1 text-sm text-gray-400 group-hover:text-gray-700"><Pencil className="w-3.5 h-3.5" /> Add</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="rounded-none border-2 border-gray-900 w-80 space-y-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1">Service / Repair Needed</div>
          <Textarea data-testid="fleet-service-input" rows={3} className="rounded-none border-2" value={text}
            placeholder="What needs done now" onChange={(e) => setText(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <Button data-testid="fleet-service-save" onClick={save} disabled={busy}
            className="flex-1 rounded-none bg-gray-900 hover:bg-gray-700 text-white font-bold uppercase text-xs">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Save
          </Button>
          <Button data-testid="fleet-service-log" onClick={logDone} disabled={busy || !cur} variant="outline"
            className="flex-1 rounded-none border-2 border-[#00913C] text-[#00913C] hover:bg-[#00913C] hover:text-white font-bold uppercase text-xs">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Done → Log
          </Button>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-gray-500 mb-1">History</div>
          {(row.repair_logs && row.repair_logs.length)
            ? <ul className="text-sm list-disc pl-4 max-h-40 overflow-y-auto">{[...row.repair_logs].reverse().map((l, i) => <li key={i}>{l}</li>)}</ul>
            : <div className="text-sm text-gray-400">No repairs logged yet</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const colName = { key: "name", label: "Z #", render: (r) => (
  <TooltipProvider delayDuration={100}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span data-testid="fleet-zno" className="font-bold underline decoration-dotted decoration-gray-300 underline-offset-4 cursor-help">{r.name || "—"}</span>
      </TooltipTrigger>
      <TooltipContent className="rounded-none border-2 border-gray-900 bg-white text-gray-900 max-w-xs whitespace-normal">
        <div className="font-bold uppercase tracking-wide text-[10px] text-gray-500">Service History</div>
        <div className="text-sm">Last serviced: {r.date_serviced ? fmtDate(r.date_serviced) : "—"}</div>
        {(r.repair_logs && r.repair_logs.length) ? (
          <ul className="text-sm list-disc pl-4 mt-1">{r.repair_logs.slice(0, 6).map((l, i) => <li key={i}>{l}</li>)}</ul>
        ) : <div className="text-sm text-gray-400 mt-1">No repairs logged</div>}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
) };
const colModel = { key: "vehicle_type", label: "Model", render: (r) => <span className="font-medium">{r.vehicle_type || "—"}</span> };
const colStatus = { key: "status", label: "Status", editable: true, options: STATUS, render: (r) => <StatusBadge status={r.status} /> };
const colStage = { key: "stage", label: "Stage", editable: true, options: STAGE, render: (r) => <StatusBadge status={r.stage} /> };
const colMileage = { key: "mileage", label: "Mileage", align: "right", render: (r) => <InlineEdit row={r} path="fleet" field="mileage" type="number" label="Mileage" naField="mileage_na" render={(x) => <span className="text-sm">{(x.mileage || 0).toLocaleString()}</span>} /> };
const colServiceDue = { key: "service_due_miles", label: "Service Due (mi)", align: "right", render: (r) => <InlineEdit row={r} path="fleet" field="service_due_miles" type="number" label="Service Due (mi)" render={(x) => x.service_due_miles ? <span className="text-sm">{Number(x.service_due_miles).toLocaleString()}</span> : <span className="text-gray-300">—</span>} /> };
const colDateServiced = { key: "date_serviced", label: "Date Serviced", render: (r) => <InlineEdit row={r} path="fleet" field="date_serviced" type="date" label="Date Serviced" render={(x) => x.date_serviced ? <span className="text-sm">{fmtDate(x.date_serviced)}</span> : <span className="text-gray-300">—</span>} /> };
const colRepairs = { key: "service_needed", label: "Service / Repair Needed", render: (r) => <ServiceCell row={r} /> };
const colHours = { key: "hours", label: "Hours", align: "right", render: (r) => <InlineEdit row={r} path="fleet" field="hours" type="number" label="Hours" render={(x) => <span className="text-sm">{(x.hours || 0).toLocaleString()}</span>} /> };
const colServiceDueHours = { key: "service_due_hours", label: "Service Due (hrs)", align: "right", render: (r) => <InlineEdit row={r} path="fleet" field="service_due_hours" type="number" label="Service Due (hours)" render={(x) => x.service_due_hours ? <span className="text-sm">{Number(x.service_due_hours).toLocaleString()}</span> : <span className="text-gray-300">—</span>} /> };
const colLastInsp = (label) => ({ key: "last_inspection", label, render: (r) => <InlineEdit row={r} path="fleet" field="last_inspection" type="date" label="Last Inspection" naField="dot_na" render={(x) => x.last_inspection ? <span className="text-sm">{fmtDate(x.last_inspection)}</span> : <span className="text-gray-300">—</span>} /> });
const colNextInsp = (label) => ({ key: "next_inspection_due", label, render: (r) => <InlineEdit row={r} path="fleet" field="next_inspection_due" type="date" label={label} naField="dot_na" render={(x) => <InspectionBadge date={x.next_inspection_due} />} /> });
const colNotes = { key: "notes", label: "Notes", render: (r) => <NoteCell row={r} path="fleet" /> };
const colInitials = { key: "initials", label: "Entered By", render: (r) => <span className="font-mono-data uppercase">{r.initials || "—"}</span> };
const colAttach = { key: "attachments", label: "Files", render: (r) => <AttachmentsCell row={r} path="fleet" /> };
const colPlate = { key: "license_plate", label: "Plate", render: (r) => <InlineEdit row={r} path="fleet" field="license_plate" type="text" label="License Plate" render={(x) => x.license_plate ? <span className="text-sm">{x.license_plate}</span> : <span className="text-gray-300">—</span>} /> };
const colType = { key: "vehicle_type", label: "Type", render: (r) => <span className="font-medium">{r.vehicle_type || "—"}</span> };

const fieldName = { key: "name", label: "Z Number", type: "text", required: true, placeholder: "e.g. Z-1234" };
const fieldModel = { key: "vehicle_type", label: "Model / Make", type: "text", required: true, placeholder: "e.g. Chevy Silverado 1500" };
const fieldStatus = { key: "status", label: "Status", type: "select", default: "up_to_date", required: true, options: STATUS };
const fieldStage = { key: "stage", label: "Stage", type: "select", default: "scheduled", required: true, options: STAGE };
const fieldMileage = { key: "mileage", label: "Mileage", type: "number", required: true };
const fieldServiceDue = { key: "service_due_miles", label: "Service Due (mi)", type: "number", required: true, placeholder: "190000" };
const fieldDateServiced = { key: "date_serviced", label: "Date Serviced (leave blank if not done yet)", type: "date" };
const fieldRepairs = { key: "service_needed", label: "Service / Repair Needed (freely replaceable)", type: "text", full: true, placeholder: "What needs done now — log it to history when finished" };
const fieldNotes = { key: "notes", label: "Notes", type: "textarea", full: true };
const fieldInitials = { key: "initials", label: "Your Initials", type: "text", required: true, maxLength: 2, placeholder: "e.g. BW" };

const TABS = [
  {
    key: "service_trucks", label: "Service Trucks",
    fields: [fieldName, { key: "assigned_to", label: "Assigned Tech", type: "text", required: true, placeholder: "e.g. Boone" }, fieldMileage, fieldDateServiced, fieldServiceDue, fieldStatus, fieldStage, fieldRepairs, fieldNotes],
    columns: [colName, { key: "assigned_to", label: "Assigned Tech", render: (r) => <span className="font-medium">{r.assigned_to || "—"}</span> }, colMileage, colDateServiced, colServiceDue, colStatus, colStage, colRepairs, colNotes, colAttach],
  },
  {
    key: "unassigned", label: "Unassigned",
    fields: [fieldName, fieldModel, fieldMileage, fieldDateServiced, fieldServiceDue, fieldStatus, fieldStage, fieldRepairs, fieldNotes],
    columns: [colName, colModel, colMileage, colDateServiced, colServiceDue, colStatus, colStage, colRepairs, colNotes, colAttach],
  },
  {
    key: "assigned", label: "Assigned",
    fields: [fieldName, fieldModel, { key: "assigned_to", label: "Assigned To", type: "text", required: true, placeholder: "Person's name" }, fieldMileage, fieldDateServiced, fieldServiceDue, fieldStatus, fieldStage, fieldRepairs, fieldNotes],
    columns: [colName, colModel, { key: "assigned_to", label: "Assigned To", render: (r) => <span className="font-medium">{r.assigned_to || "—"}</span> }, colMileage, colDateServiced, colServiceDue, colStatus, colStage, colRepairs, colNotes, colAttach],
  },
  {
    key: "bryan", label: "Bryan",
    fields: [fieldName, fieldModel, fieldMileage, fieldDateServiced, fieldServiceDue, fieldStatus, fieldStage, fieldRepairs, fieldNotes],
    columns: [colName, colModel, colMileage, colDateServiced, colServiceDue, colStatus, colStage, colRepairs, colNotes, colAttach],
  },
  {
    key: "trailers_equipment", label: "Trucking & Hauling",
    fields: [fieldName, { key: "vehicle_type", label: "Type", type: "text", required: true, placeholder: "gooseneck / flatbed / semi / semi trailer" }, { key: "license_plate", label: "License Plate", type: "text" }, { key: "mileage", label: "Mileage (semis only — blank for trailers)", type: "number" }, { key: "last_inspection", label: "Last Inspection / DOT (leave blank if not done)", type: "date" }, { key: "next_inspection_due", label: "Next Inspection / DOT Due", type: "date" }, fieldStatus, fieldStage, fieldRepairs, fieldNotes],
    columns: [colName, colType, colPlate, colMileage, colLastInsp("Last Insp./DOT"), colNextInsp("Next Insp./DOT Due"), colStatus, colStage, colRepairs, colNotes, colAttach],
  },
  {
    key: "forklifts", label: "Forklifts/Mowers",
    fields: [fieldName, fieldModel, { key: "hours", label: "Hours", type: "number", required: true }, { key: "service_due_hours", label: "Service Due (hours)", type: "number", required: true }, fieldDateServiced, { key: "next_inspection_due", label: "Next Inspection Due", type: "date" }, fieldStatus, fieldStage, fieldRepairs, fieldNotes],
    columns: [colName, colModel, colHours, colServiceDueHours, colDateServiced, colNextInsp("Next Insp. Due"), colStatus, colStage, colRepairs, colNotes, colAttach],
  },
];

export default function Fleet() {
  const { search } = useLocation();
  const tabParam = new URLSearchParams(search).get("tab");
  const initialTab = TABS.some((t) => t.key === tabParam) ? tabParam : "service_trucks";
  return (
    <div data-testid="fleet-section">
      <PageHeader title="Fleet" subtitle="Company vehicles, trailers and trucking equipment" />
      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="mb-6 flex flex-wrap h-auto justify-start gap-2 bg-transparent p-0">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={`fleet-tab-${t.key}`}
              className="rounded-none border-2 border-gray-900 px-4 py-2 font-bold uppercase tracking-wide text-xs data-[state=active]:bg-gray-900 data-[state=active]:text-white"
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-0">
            <CrudSection
              title={t.label}
              subtitle=""
              path="fleet"
              category={t.key}
              testPrefix={`fleet-${t.key}`}
              addLabel={`New ${t.label.replace(/s$/, "")}`}
              editLabel={`Edit ${t.label.replace(/s$/, "")}`}
              fields={t.fields}
              columns={t.columns}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
